/*
 * hp-analytics-events — the theme's actual GA4 events.
 *
 * The dispatcher (hp-analytics.js) is generic and knows nothing about carts,
 * products or drawers. THIS file is where the theme's own events live, so
 * adding an area never means editing the transport, and reading the transport
 * never means wading through business rules.
 *
 * Events that can be expressed as markup should use `data-hp-track` instead
 * (see snippets/hp-analytics-attrs.liquid) — that needs no code at all. A
 * handler belongs here only when the event needs a decision or a live value
 * that Liquid cannot know at render time. `cart_icon_click` needs both.
 *
 * ── EVENTS ─────────────────────────────────────────────────────────────────
 *   cart_icon_click        Header cart icon clicked to OPEN the drawer.
 *                          Params: cart_item_count.                      (P1)
 *   add_to_cart            PDP Add to Cart, on the SUCCESSFUL add.
 *                          Params: currency, value, items[], atc_source. (P0)
 *   select_variant         A PDP variant option changed.
 *                          Params: item_id, variant_name, variant_value. (P0)
 *   view_cart              The cart drawer opened.
 *                          Params: currency, value, items[].             (P0)
 *   begin_checkout         The standard Checkout button pressed.
 *                          Params: currency, value, items[],
 *                          checkout_method.                              (P0)
 *   select_purchase_type   The PDP subscribe / one-time toggle changed.
 *                          Params: purchase_type, discount_pct.          (P0)
 *
 * `view_item` (PDP) and `view_item_list` (collection) are NOT here: they fire
 * because markup rendered, not because anything was done, so they are declared
 * in Liquid with snippets/hp-analytics-view.liquid and fired by the dispatcher's
 * view binding. Nothing in this file has to know they exist.
 *
 * ── WHAT IS DELIBERATELY NOT COVERED ───────────────────────────────────────
 * The mobile STICKY Add to Cart bar and the EXPRESS checkout buttons both have
 * P0 rows in the tracking plan and neither exists in this theme — the sticky bar
 * was never built, and the express buttons were removed from both cart surfaces
 * (see the comment in sections/main-cart-footer.liquid). There is no handler for
 * either, on purpose. If either component lands, its event belongs here:
 * `atc_source: 'sticky_bar'` and `checkout_method: 'express'` respectively.
 *
 * Checkout, payment and purchase cannot come from a theme at all. See the
 * "WHAT THIS LAYER CANNOT SEE" note in snippets/hp-analytics.liquid.
 */
(function () {
  /*
   * The two shapes the header's cart icon takes, from sections/header.liquid:
   *
   *   summary  the drawer trigger, when the cart drawer is enabled and we are
   *            not already on the cart page
   *   link     the plain <a href="/cart"> fallback, used on the cart page and
   *            when the drawer setting is off
   *
   * Both are "the cart icon", so both fire. The link case navigates rather than
   * opening a drawer, which is worth knowing when reading the numbers: on the
   * cart page this event means "clicked through to the cart it is already on".
   * Say so rather than silently counting two different gestures as one.
   */
  const CART_SUMMARY = 'cart-drawer details.cart-drawer-container > summary';
  const CART_LINK = 'a#cart-icon-bubble';

  /*
   * ── WHY THE COUNT IS READ FROM THE DOM ────────────────────────────────────
   * `cart.item_count` cannot be baked into the icon at render time. The
   * clickable element lives in sections/header.liquid and is NOT re-rendered by
   * a cart update — only the bubble INSIDE it is (cart.js lists
   * `cart-icon-bubble` in getSectionsToRender). A Liquid-rendered attribute on
   * the icon would therefore be right on load and wrong after the first add.
   *
   * Subscribing to PUB_SUB_EVENTS.cartUpdate does not fix it either: `AddToCart`
   * in global.js re-renders those sections directly and publishes nothing, so a
   * JS-side counter would miss every add — the most common cart change there is.
   *
   * The bubble is the one thing EVERY path refreshes, which makes it the real
   * source of truth. It carries `data-hp-cart-count` (added in header.liquid and
   * its Section Rendering twin, sections/cart-icon-bubble.liquid — both, per the
   * standing rule that those two must agree).
   *
   * Reading the VISIBLE number instead would have been wrong at 100+, where the
   * markup drops the numeral and renders a dot; the attribute carries the real
   * figure at every size. No bubble at all means an empty cart, which is 0 — the
   * markup omits the whole element when `cart == empty`.
   */
  function cartItemCount() {
    const bubble = document.querySelector('#cart-icon-bubble [data-hp-cart-count]');
    if (!bubble) return 0;
    const parsed = parseInt(bubble.getAttribute('data-hp-cart-count'), 10);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  document.addEventListener('click', function (event) {
    const target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    const summary = target.closest(CART_SUMMARY);
    if (summary) {
      /*
       * ONLY WHEN OPENING. The same summary closes the drawer, and a close is
       * not a cart-icon click in the sense this event means — counted, it would
       * roughly double the number for anyone who dismisses the drawer with the
       * icon rather than the X or the scrim.
       *
       * `details.open` is still the PRE-click value inside a click listener,
       * because <details> toggles as the default action, after the event has
       * finished bubbling. So `open === true` here means the click is closing it.
       */
      const details = summary.parentElement;
      if (details && details.open) return;

      window.hpTrack('cart_icon_click', { cart_item_count: cartItemCount() });
      return;
    }

    if (target.closest(CART_LINK)) {
      window.hpTrack('cart_icon_click', { cart_item_count: cartItemCount() });
    }
  });

  /* ══════════════════════════════════════════════════════════════════════════
   * SHARED READERS
   *
   * Every payload below is server-rendered into an ESCAPED JSON attribute, for
   * the reason hp-analytics-attrs.liquid documents: an attribute cannot be
   * broken out of by a product title, and it is the one escaping rule the theme
   * already has. All of them are read at the moment an event fires, never
   * cached — Section Rendering replaces this markup constantly, and a reference
   * held from page load would point at a detached node describing an old cart.
   * ═══════════════════════════════════════════════════════════════════════ */

  function readAttrJSON(el, attr) {
    if (!el) return null;
    const raw = el.getAttribute(attr);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  function readTextJSON(el) {
    if (!el) return null;
    try {
      const parsed = JSON.parse(el.textContent);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  /*
   * The PDP's id-keyed variant map, rendered by sections/hp-main-product.liquid.
   * Its ABSENCE is meaningful and is used as a guard below: no map means this
   * page is not a PDP, so an add that happened here belongs to some other
   * surface and is not ours to report.
   */
  function variantMap() {
    return readAttrJSON(
      document.querySelector('[data-hp-product-variants]'),
      'data-hp-product-variants'
    );
  }

  function pdpForm() {
    return document.querySelector('.hp-pdp product-form form');
  }

  /* ══════════════════════════════════════════════════════════════════════════
   * add_to_cart  (tracking plan ID 5, P0)
   *
   * ── FIRED ON THE SUCCESSFUL ADD, NOT ON THE CLICK ────────────────────────
   * The plan says "fires on Add to Cart click", and a click listener was the
   * obvious build. It over-reports: the button is clickable in states that add
   * nothing — a required field left empty, a variant that sold out between page
   * load and the press, any network failure — and every one of those would land
   * in GA4 as an add that never happened. Cart-to-add ratios are exactly where
   * that shows up, and it shows up as the funnel looking worse than it is.
   *
   * product-form.js publishes `cartUpdate` with `source: 'product-form'` only
   * after Shopify has returned a cart, so subscribing to it costs nothing and
   * means an add_to_cart in the property is an item genuinely in a cart.
   *
   * ── AND ONLY FOR THIS PRODUCT ────────────────────────────────────────────
   * The same publish fires for a quick-add anywhere on the site. The map lookup
   * is what tells them apart: a variant that is not in THIS page's map belongs
   * to another product, and the event is left alone rather than sent with no
   * item data. That makes the guard self-maintaining — a quick-add surface that
   * wants its own add_to_cart renders its own map and gets it.
   * ═══════════════════════════════════════════════════════════════════════ */

  const ATC_SOURCE = 'main_button';

  /*
   * A SUBSCRIPTION LINE IS NOT CHARGED THE VARIANT'S PRICE. The map carries the
   * one-time price, because that is what the variant costs; when a selling plan
   * is selected the cart charges the allocation instead. Reading it from the
   * subscription picker's own payload — the same one that prices the card, the
   * headline and the CTA — means the four cannot disagree. CLAUDE.md records
   * the same trap in the cart, where `original_price` and `final_price` are
   * both the plan price and base's sale branch never fires.
   */
  function sellingPlanPriceCents(variantId, planId) {
    const data = readTextJSON(document.querySelector('.hp-pdp [data-hp-sub-data]'));
    const variant = data && data.variants && data.variants[String(variantId)];
    const plan = variant && variant.plans && variant.plans[String(planId)];
    return plan && typeof plan.price === 'number' ? plan.price : null;
  }

  /*
   * ── form.elements, NOT form.querySelector ────────────────────────────────
   * THE SELLING PLAN INPUT IS NOT INSIDE THE FORM. The subscription picker is
   * its own PDP block rendered above `buy_buttons`, so it cannot nest inside
   * `{% form 'product' %}`; it is associated by the HTML `form="<id>"`
   * attribute instead, which CLAUDE.md documents and which is what makes
   * `new FormData(form)` pick it up. `querySelector` is a DOM-tree search and
   * finds nothing.
   *
   * That is not a crash, which is what makes it worth a named helper: the
   * lookup returns null, the plan price is never applied, and a subscription
   * add reports the FULL price. Measured before the fix — a 15%-off plan, two
   * units of a $35 product, reported as 70.00 instead of 59.50. Silent, and
   * wrong in the direction that overstates revenue.
   *
   * `form.elements` is the form's own control collection and includes every
   * form-associated control wherever it sits in the document.
   */
  function formField(form, name) {
    return (form && form.elements && form.elements.namedItem(name)) || null;
  }

  function onProductFormAdd(variantId) {
    const map = variantMap();
    const entry = map && map.variants && map.variants[String(variantId)];
    if (!entry || !entry.item) return;

    const form = pdpForm();
    const qtyField = formField(form, 'quantity');
    const parsedQty = parseInt(qtyField && qtyField.value, 10);
    const quantity = parsedQty > 0 ? parsedQty : 1;

    /* Copied, not mutated: the map is read again by select_variant and by the
       next add, and writing a quantity into it would leak into both. */
    const item = Object.assign({}, entry.item, { quantity: quantity });

    const planField = formField(form, 'selling_plan');
    const planId = planField && planField.value;
    if (planId) {
      const cents = sellingPlanPriceCents(variantId, planId);
      if (cents !== null) item.price = cents / 100;
    }

    const params = {
      currency: map.currency,
      items: [item],
      atc_source: ATC_SOURCE
    };
    if (typeof item.price === 'number') {
      /* Rounded because floating point turns 29.75 * 3 into 89.25000000000001,
         and GA4 stores the number it is given. */
      params.value = Math.round(item.price * quantity * 100) / 100;
    }

    window.hpTrack('add_to_cart', params);
  }

  /*
   * `subscribe` and `PUB_SUB_EVENTS` come from pubsub.js and global.js, both
   * loaded (deferred) ahead of this file in layout/theme.liquid, so they exist
   * by the time this runs. Guarded anyway: this file must never be the reason a
   * page throws, and a theme where pubsub is absent should lose one event
   * rather than every event.
   */
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (!event || event.source !== 'product-form') return;
      onProductFormAdd(event.productVariantId);
    });
  }

  /* ══════════════════════════════════════════════════════════════════════════
   * select_variant  (tracking plan ID 5, P0)
   *
   * The plan calls this `select_item`. It is deliberately renamed: `select_item`
   * is GA4's standard event for choosing something out of a LIST, and the plan
   * also assigns that name to three other rows (Frequently Bought Together, PDP
   * recommendations, cart cross-sell). Four different gestures under one name
   * collapse into a single report row separable only by parameter, and a swatch
   * click is not a list selection at all. Agreed with the team before building.
   *
   * ── RESOLVING THE VARIANT ────────────────────────────────────────────────
   * A swatch click knows an option VALUE, not a variant. Reading the form's
   * `[name="id"]` would give the PREVIOUS variant, because product-info.js
   * updates it after an async fetch that has not happened yet at `change` time.
   * So the full set of currently-checked option values is gathered from the
   * picker and matched against the map — which is how Shopify resolves a variant
   * itself, is synchronous, and cannot race anything.
   * ═══════════════════════════════════════════════════════════════════════ */

  const OPTION_WRAP = '[data-hp-option-name]';

  function currentOptionValue(wrap) {
    const checked = wrap.querySelector('input[type="radio"]:checked');
    if (checked) return checked.value;
    const select = wrap.querySelector('select');
    return select ? select.value : null;
  }

  function findVariantByOptions(map, values) {
    if (!map || !map.variants) return null;
    const ids = Object.keys(map.variants);
    for (let i = 0; i < ids.length; i++) {
      const entry = map.variants[ids[i]];
      const options = entry && entry.options;
      if (!options || options.length !== values.length) continue;
      let matched = true;
      for (let j = 0; j < options.length; j++) {
        if (String(options[j]) !== String(values[j])) {
          matched = false;
          break;
        }
      }
      if (matched) return entry;
    }
    return null;
  }

  document.addEventListener('change', function (event) {
    const field = event.target;
    if (!field || typeof field.closest !== 'function') return;

    const wrap = field.closest(OPTION_WRAP);
    if (!wrap) return;

    const picker = wrap.closest('variant-selects');
    if (!picker) return;

    const value = currentOptionValue(wrap);
    if (value === null) return;

    const params = {
      /*
       * Lower-cased so the dimension is one value rather than however the
       * merchant capitalised the option. The plan's own example is 'color'.
       */
      variant_name: (wrap.getAttribute('data-hp-option-name') || '').toLowerCase(),
      variant_value: value
    };

    /*
     * The option order in the DOM is the option order in `variant.options`,
     * because both come from `product.options_with_values`.
     */
    const wraps = Array.prototype.slice.call(picker.querySelectorAll(OPTION_WRAP));
    const entry = findVariantByOptions(variantMap(), wraps.map(currentOptionValue));

    /*
     * Sent WITHOUT item_id when no variant matches — an unavailable combination
     * is still a real choice the shopper made, and dropping the event would
     * hide exactly the combinations worth knowing about. Omitted rather than
     * sent empty, per the convention in hp-analytics-item.
     */
    if (entry && entry.item) params.item_id = entry.item.item_id;

    window.hpTrack('select_variant', params);
  });

  /* ══════════════════════════════════════════════════════════════════════════
   * view_cart  (tracking plan ID 6, P0)
   *
   * ── WATCHING THE `open` ATTRIBUTE, NOT A CLICK ───────────────────────────
   * The drawer opens three ways: the header icon's <summary>, `openMenuDrawer`
   * after an add to cart, and any future programmatic call. All three end at the
   * same place — the `open` attribute on the drawer's <details> — so watching
   * that covers every path and cannot be bypassed by a new caller. A click
   * listener on the icon would have counted only the first, and would also have
   * counted the click that CLOSES it.
   * ═══════════════════════════════════════════════════════════════════════ */

  function cartPayloadWithin(el) {
    return readAttrJSON(
      el && el.querySelector('[data-hp-cart-payload]'),
      'data-hp-cart-payload'
    );
  }

  function fireViewCart() {
    const payload =
      cartPayloadWithin(document.getElementById('mini-cart')) || cartPayloadWithin(document);
    if (!payload) return false;
    window.hpTrack('view_cart', {
      currency: payload.currency,
      value: payload.value,
      items: payload.items
    });
    return true;
  }

  const cartDetails = document.querySelector('cart-drawer details.cart-drawer-container');

  if (cartDetails) {
    /*
     * ── SETTING AN ATTRIBUTE TO THE VALUE IT ALREADY HAS STILL FIRES THE
     *    OBSERVER, so the state has to be tracked and the EDGE detected. ─────
     * Opening the drawer from the header icon produces TWO records one
     * millisecond apart, both with `open === true`:
     *
     *     old: null -> ""    the native <details> toggle, the summary's
     *                        default action
     *     old: ""   -> ""    MenuDrawer.openMenuDrawer's own
     *                        setAttribute('open', ''), which is redundant on
     *                        this path and harmless to everything except an
     *                        observer counting transitions
     *
     * Firing on each record sent `view_cart` twice per open — reported from the
     * console, and it would have doubled the event in GA4 with nothing in the
     * numbers to say why. A boolean is the whole fix: only a genuine
     * closed -> open transition is a cart view.
     *
     * `attributeOldValue` would NOT have helped. The second record's old value
     * is "" and its new value is "", which is indistinguishable from a no-op
     * re-set; what matters is the state this handler last acted on, not what
     * the attribute was a moment ago.
     */
    let cartWasOpen = cartDetails.open;
    let pendingOpenListener = null;

    new MutationObserver(function () {
      const isOpen = cartDetails.open;
      if (isOpen === cartWasOpen) return;
      cartWasOpen = isOpen;

      if (!isOpen) {
        /*
         * A drawer closed before its first fetch landed has a listener waiting
         * for a payload that is no longer being looked at. Dropped, so a later
         * `cartdrawer:opened` cannot fire a view for a shut drawer.
         */
        if (pendingOpenListener) {
          document.removeEventListener('cartdrawer:opened', pendingOpenListener);
          pendingOpenListener = null;
        }
        return;
      }

      if (fireViewCart()) return;

      /*
       * FIRST OPEN ONLY: <mini-cart> fetches its own contents on the
       * IntersectionObserver that fires when the panel becomes visible, so on
       * the very first open the payload does not exist yet. `cartdrawer:opened`
       * is dispatched by cart-drawer.js when that fetch lands, which is the
       * exact moment the payload appears.
       */
      pendingOpenListener = function () {
        document.removeEventListener('cartdrawer:opened', pendingOpenListener);
        pendingOpenListener = null;
        fireViewCart();
      };
      document.addEventListener('cartdrawer:opened', pendingOpenListener);
    }).observe(cartDetails, { attributes: true, attributeFilter: ['open'] });
  }

  /* ══════════════════════════════════════════════════════════════════════════
   * begin_checkout  (tracking plan ID 6, P0)
   *
   * `checkout_method` is always 'standard'. The plan's 'express' row covers the
   * Shop Pay / Apple Pay / Google Pay buttons, which this theme removed from
   * both cart surfaces — so every checkout that starts from the storefront
   * starts here, and hardcoding the value states that rather than leaving a
   * dimension that is silently never populated.
   * ═══════════════════════════════════════════════════════════════════════ */

  /*
   * Walks up until an ancestor CONTAINS a payload. The two surfaces nest
   * differently — in the drawer the button and the payload share the cart form,
   * on the cart page they are siblings in the footer — and a fixed selector that
   * suits one silently returns the other's cart. Walking finds the nearest
   * enclosing payload on both without naming either.
   */
  function cartPayloadNear(el) {
    let node = el;
    while (node && node !== document.documentElement) {
      const found = cartPayloadWithin(node);
      if (found) return found;
      node = node.parentElement;
    }
    return cartPayloadWithin(document);
  }

  document.addEventListener('click', function (event) {
    const target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    const button = target.closest('[name="checkout"]');
    if (!button) return;
    /* An empty cart renders the button disabled; a disabled press is not intent. */
    if (button.disabled) return;

    const payload = cartPayloadNear(button);
    if (!payload) return;

    window.hpTrack('begin_checkout', {
      currency: payload.currency,
      value: payload.value,
      items: payload.items,
      checkout_method: 'standard'
    });
  });

  /* ══════════════════════════════════════════════════════════════════════════
   * select_purchase_type  (tracking plan ID 10, P0)
   *
   * Only the MODE radios, not the frequency picker — the plan's trigger is the
   * One-Time / Subscribe toggle, and a shopper changing from monthly to weekly
   * has not changed purchase type. If cadence is wanted it is its own event.
   *
   * The control's own values are 'subscribe' and 'onetime'; they are reported as
   * 'subscription' and 'one_time' so the dimension reads as data rather than as
   * this component's internal spelling.
   * ═══════════════════════════════════════════════════════════════════════ */

  /*
   * discount_pct is read from the subscription picker's server-rendered payload
   * rather than from the badge it draws. The badge is UI text ("15% Off") and
   * parsing it would put a number GA4 reports on at the mercy of a copy change;
   * `percent` is computed in Liquid from the allocation, which is the same
   * figure the cart will charge against.
   */
  function subscriptionDiscountPct(root) {
    const data = readTextJSON(root.querySelector('[data-hp-sub-data]'));
    if (!data) return 0;

    const form = document.getElementById(root.dataset.form);
    const variantField = formField(form, 'id');
    const variantId = variantField && variantField.value;

    const planField = root.querySelector('[name^="hp-sub-plan-"]:checked');
    const planId = (planField && planField.value) || (data.plans && data.plans[0]);

    const variant = data.variants && data.variants[String(variantId)];
    const plan = variant && variant.plans && variant.plans[String(planId)];
    return plan && typeof plan.percent === 'number' ? plan.percent : 0;
  }

  document.addEventListener('change', function (event) {
    const field = event.target;
    if (!field || !field.name || field.name.indexOf('hp-sub-mode-') !== 0) return;

    const root = field.closest('hp-subscription');
    if (!root) return;

    const subscribing = field.value === 'subscribe';

    window.hpTrack('select_purchase_type', {
      purchase_type: subscribing ? 'subscription' : 'one_time',
      /* Zero for one-time rather than omitted: "no discount" is a real value
         here, and an absent parameter would read as "not measured". */
      discount_pct: subscribing ? subscriptionDiscountPct(root) : 0
    });
  });
})();
